import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Avatar, ButtonLink, Chip, EmptyState, PageTitle, Spinner } from '../components/ui'
import { cx } from '../lib/cx'
import { type Season, messageOf } from '../lib/api'
import { seasonsQuery } from '../lib/queries'

// Every season the league has played, newest first, each with its podium
// (the top three, champion first, ties sharing a rank; the server computes
// it) and the way to its table and its results
// (designs/20261017-seasons.md §5).
export const Route = createFileRoute('/l/$slug/seasons')({ component: Seasons })

function Seasons() {
  const { slug } = Route.useParams()
  const q = useQuery(seasonsQuery(slug))
  if (q.isPending) return <div className="grid place-items-center py-16 text-muted"><Spinner /></div>
  if (q.isError) return <EmptyState title="Couldn't load seasons">{messageOf(q.error)}</EmptyState>
  const seasons = [...q.data].reverse()
  return (
    <>
      <PageTitle eyebrow="League" aside={<Chip tone="done">{seasons.length} {seasons.length === 1 ? 'season' : 'seasons'}</Chip>}>
        Seasons
      </PageTitle>
      <ul className="grid gap-3">
        {seasons.map((s) => (
          <li key={s.number}>
            <SeasonCard slug={slug} season={s} />
          </li>
        ))}
      </ul>
    </>
  )
}

function SeasonCard({ slug, season: s }: { slug: string; season: Season }) {
  const podium = s.podium ?? []
  const running = s.state === 'running'
  const champions = podium.filter((p) => p.is_champion)
  const date = new Date(running ? s.started_at : (s.ended_at ?? s.started_at)).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
  return (
    <article aria-label={s.name} className="grid gap-3 rounded-card border border-line bg-surface p-4">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate text-[20px] font-extrabold leading-tight tracking-[-.02em]">{s.name}</h2>
          <p className="text-[13px] text-muted">
            {running ? `Round ${Math.min(s.rounds_final + 1, s.rounds)} of ${s.rounds}, since ${date}` : `${s.rounds_final} ${s.rounds_final === 1 ? 'round' : 'rounds'}, ended ${date}`}
          </p>
        </div>
        <Chip tone={running ? 'live' : 'done'}>{running ? 'Running' : 'Complete'}</Chip>
      </header>
      {podium.length === 0 ? (
        <p className="text-[14.5px] text-muted">{running ? 'Nobody has scored yet.' : 'Nobody scored, so there was no champion.'}</p>
      ) : (
        <ol className="grid gap-1" aria-label={running ? 'Top three so far' : 'Podium'}>
          {podium.map((p) => (
            <li key={p.manager.id} className={cx('flex items-center gap-2.5 rounded-field px-2 py-1.5', p.is_me && 'bg-accent-wash')}>
              <span className="w-6 font-score text-[22px] font-bold text-muted" aria-label={`Rank ${p.rank}`}>{p.rank}</span>
              <Avatar src={p.manager.avatar_url} />
              <span className="min-w-0 flex-1 truncate text-[15px]">
                <b className="font-semibold">{p.manager.display_name}</b>
                {!running && p.is_champion && <Chip tone="live" className="ml-2 !py-[2px] !text-[11.5px]">{champions.length > 1 ? 'co-champion' : 'champion'}</Chip>}
              </span>
              <span className="font-score text-[22px] font-bold">{p.total}</span>
            </li>
          ))}
        </ol>
      )}
      <div className="grid grid-cols-2 gap-2">
        <ButtonLink to="/l/$slug/s/$n/standings" params={{ slug, n: s.number }} size="sm" block>
          {running ? 'Standings' : 'Final table'}
        </ButtonLink>
        <ButtonLink to="/l/$slug/s/$n/results" params={{ slug, n: s.number }} size="sm" block>
          Results
        </ButtonLink>
      </div>
    </article>
  )
}
